import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import {
  X, Loader2, Shield, Flag, CheckCircle, AlertTriangle, FileCheck, TrendingUp,
  ArrowLeftRight
} from 'lucide-react';
import { toast } from 'sonner';
import { COUNTRIES } from './utils';
import { apiPost, ApiError } from '../../lib/apiClient';

export default function OriginTab({ user, setUser, setActiveTab }) {
  // Origin & Preference state
  const [originForm, setOriginForm] = useState({
    description: '',
    country_of_origin: 'China',
    product_category: 'electronics',
    certificate_of_origin: false,
    manufacturer_info: false,
    commercial_invoice: false,
    previous_origin: ''
  });
  const [originLoading, setOriginLoading] = useState(false);
  const [originResults, setOriginResults] = useState(null);
  const [scenarioForm, setScenarioForm] = useState({
    current_origin: 'China',
    alternatives: ['Mexico', 'Vietnam', 'South Korea'],
    unit_cost: '',
    annual_volume: ''
  });
  const [scenarioResults, setScenarioResults] = useState(null);
  const [scenarioLoading, setScenarioLoading] = useState(false);


  const handleOriginValidation = async (e) => {
    e.preventDefault();
    
    if (!originForm.description || !originForm.country_of_origin) {
      toast.error('Please fill in product description and origin country');
      return;
    }

    setOriginLoading(true);
    setOriginResults(null);

    try {
      const data = await apiPost('/api/origin/validate', {
        country_of_origin: originForm.country_of_origin,
        product_category: originForm.product_category,
        product_description: originForm.description,
        certificate_of_origin: originForm.certificate_of_origin,
        manufacturer_info: originForm.manufacturer_info,
        commercial_invoice: originForm.commercial_invoice,
        previous_origin: originForm.previous_origin || null,
      });
      // Normalize backend AI schema → shape the results panel expects.
      // Live schema (gemini-3-flash): confidence_score is 0-100 int, risk_factors is string[],
      // best_option is a string, and rate details live in eligible_agreements[0].
      const riskLevelMap = { low: 'LOW', medium: 'MEDIUM', high: 'HIGH', minimal: 'MINIMAL', unknown: 'UNKNOWN' };
      const originVal = data?.origin_validation || {};
      const ftaElig = data?.fta_eligibility || {};
      const best = ftaElig.best_option;
      const bestIsObject = best && typeof best === 'object';
      const primaryAgreement = Array.isArray(ftaElig.eligible_agreements) && ftaElig.eligible_agreements.length > 0
        ? ftaElig.eligible_agreements[0]
        : null;

      // risk_score: accept either 0-1 float or 0-100 int/percentage
      const rawConf = originVal.confidence_score;
      let riskScore = originVal.risk_score;
      if (typeof riskScore !== 'number') {
        if (typeof rawConf === 'number') {
          riskScore = rawConf <= 1 ? Math.round(rawConf * 100) : Math.round(rawConf);
        } else {
          riskScore = 0;
        }
      }

      // identified_risks: JSX reads {description, severity, mitigation}; live output is string[]
      const rawRisks = originVal.identified_risks || originVal.risk_factors || [];
      const normalizedRisks = rawRisks.map((r) =>
        typeof r === 'string'
          ? { description: r, severity: originVal.risk_level || 'UNKNOWN', mitigation: '' }
          : r
      );

      const normalized = {
        ...data,
        origin_validation: {
          ...originVal,
          risk_indicator: riskLevelMap[String(originVal.risk_level || '').toLowerCase()] || (originVal.risk_level || 'UNKNOWN'),
          risk_score: Math.max(0, Math.min(100, riskScore)),
          identified_risks: normalizedRisks,
        },
        fta_eligibility: {
          ...ftaElig,
          fta_eligible: !!(
            (bestIsObject && (best.fta_eligible ?? true)) ||
            ftaElig.fta_eligible ||
            (primaryAgreement && (
              String(primaryAgreement.status || '').toUpperCase() === 'ELIGIBLE' ||
              (typeof primaryAgreement.savings_potential === 'number' && primaryAgreement.savings_potential > 0) ||
              (typeof primaryAgreement.preferential_rate === 'number' &&
                typeof primaryAgreement.standard_rate === 'number' &&
                primaryAgreement.preferential_rate < primaryAgreement.standard_rate)
            ))
          ),
          // If best_option is a string, keep it for text display; the JSX handles both via safe access.
          applicable_fta: bestIsObject ? best : (primaryAgreement || null),
          applicable_fta_label: bestIsObject ? (best.name || best.code || '') : (typeof best === 'string' ? best : (primaryAgreement?.agreement || '')),
          mfn_rate: ftaElig.mfn_rate ?? primaryAgreement?.standard_rate ?? (bestIsObject ? best.mfn_rate : 0) ?? 0,
          preferential_rate: ftaElig.preferential_rate ?? primaryAgreement?.preferential_rate ?? (bestIsObject ? best.preferential_rate : null),
          tariff_savings: ftaElig.tariff_savings ?? primaryAgreement?.savings_potential ?? (bestIsObject ? best.tariff_savings : 0) ?? (ftaElig.total_potential_savings ? Number(ftaElig.total_potential_savings) : 0),
          section_301_applicable: ftaElig.section_301_applicable ?? (bestIsObject ? best.section_301_applicable : false) ?? false,
          requirements: ftaElig.requirements || primaryAgreement?.requirements || (bestIsObject ? best.requirements : []) || [],
        },
      };
      setOriginResults(normalized);
      toast.success('Origin validation complete!');
    } catch (error) {
      if (error instanceof ApiError) {
        toast.error(error.message || 'Validation failed');
      } else {
        toast.error('Connection error. Please try again.');
      }
    } finally {
      setOriginLoading(false);
    }
  };

  // Handle Scenario Modeling

  const handleScenarioModeling = async () => {
    if (!originForm.description || !scenarioForm.current_origin) {
      toast.error('Please fill in product description first');
      return;
    }

    const unitCost = scenarioForm.unit_cost ? parseFloat(scenarioForm.unit_cost) : null;
    const annualVolume = scenarioForm.annual_volume ? parseInt(scenarioForm.annual_volume) : null;
    
    console.log('Scenario Modeling Request:', {
      product_description: originForm.description,
      product_category: originForm.product_category,
      current_origin: scenarioForm.current_origin,
      alternative_origins: scenarioForm.alternatives,
      unit_cost: unitCost,
      annual_volume: annualVolume,
      raw_unit_cost: scenarioForm.unit_cost,
      raw_annual_volume: scenarioForm.annual_volume
    });

    setScenarioLoading(true);
    setScenarioResults(null);

    try {
      const data = await apiPost('/api/origin/scenario', {
        product_description: originForm.description,
        product_category: originForm.product_category,
        current_origin: scenarioForm.current_origin,
        alternative_origins: scenarioForm.alternatives,
        unit_cost: unitCost,
        annual_volume: annualVolume,
      });
      console.log('Scenario Modeling Response:', data);
      setScenarioResults(data);
      toast.success('Scenario modeling complete!');
    } catch (error) {
      console.error('Scenario Modeling Error:', error);
      if (error instanceof ApiError) {
        toast.error(error.message || 'Modeling failed');
      } else {
        toast.error('Connection error. Please try again.');
      }
    } finally {
      setScenarioLoading(false);
    }
  };

  // Handle file drag events

  return (
          /* Origin & Preference Engine */
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Origin Validation Form */}
            <div className="card-modern p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#8B5CF6] to-[#EC4899] flex items-center justify-center shadow-lg">
                  <Flag className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="font-outfit text-xl font-semibold text-slate-900">
                    Origin Validation
                  </h2>
                  <p className="text-sm text-slate-500 font-jakarta">Check FTA eligibility & risk indicators</p>
                </div>
              </div>

              <form onSubmit={handleOriginValidation} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-700 font-jakarta font-medium">Product Description *</Label>
                  <Textarea
                    placeholder="e.g., Wireless Bluetooth Headphones with Noise Cancellation"
                    value={originForm.description}
                    onChange={(e) => setOriginForm({ ...originForm, description: e.target.value })}
                    className="min-h-[80px] input-modern resize-none"
                    required
                    data-testid="origin-description-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-jakarta font-medium">Country of Origin *</Label>
                    <Select
                      value={originForm.country_of_origin}
                      onValueChange={(value) => {
                        setOriginForm({ ...originForm, country_of_origin: value });
                        setScenarioForm({ ...scenarioForm, current_origin: value });
                      }}
                    >
                      <SelectTrigger className="input-modern" data-testid="origin-country-select">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map((country) => (
                          <SelectItem key={country} value={country}>{country}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-700 font-jakarta font-medium">Product Category</Label>
                    <Select
                      value={originForm.product_category}
                      onValueChange={(value) => setOriginForm({ ...originForm, product_category: value })}
                    >
                      <SelectTrigger className="input-modern" data-testid="origin-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="electronics">Electronics</SelectItem>
                        <SelectItem value="textiles">Textiles & Apparel</SelectItem>
                        <SelectItem value="footwear">Footwear</SelectItem>
                        <SelectItem value="furniture">Furniture</SelectItem>
                        <SelectItem value="machinery">Machinery</SelectItem>
                        <SelectItem value="plastics">Plastics</SelectItem>
                        <SelectItem value="automotive">Automotive</SelectItem>
                        <SelectItem value="toys">Toys</SelectItem>
                        <SelectItem value="bags">Bags & Luggage</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Documentation Checklist */}
                <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                  <p className="text-sm font-medium text-slate-700 font-jakarta flex items-center gap-2">
                    <FileCheck className="w-4 h-4 text-[#8B5CF6]" />
                    Documentation Available
                  </p>
                  <div className="grid grid-cols-1 gap-2">
                    <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                      <input 
                        type="checkbox"
                        checked={originForm.certificate_of_origin}
                        onChange={(e) => setOriginForm({ ...originForm, certificate_of_origin: e.target.checked })}
                        className="w-4 h-4 rounded border-slate-300 text-[#8B5CF6] focus:ring-[#8B5CF6]/20"
                      />
                      Certificate of Origin
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                      <input 
                        type="checkbox"
                        checked={originForm.manufacturer_info}
                        onChange={(e) => setOriginForm({ ...originForm, manufacturer_info: e.target.checked })}
                        className="w-4 h-4 rounded border-slate-300 text-[#8B5CF6] focus:ring-[#8B5CF6]/20"
                      />
                      Manufacturer Information
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                      <input 
                        type="checkbox"
                        checked={originForm.commercial_invoice}
                        onChange={(e) => setOriginForm({ ...originForm, commercial_invoice: e.target.checked })}
                        className="w-4 h-4 rounded border-slate-300 text-[#8B5CF6] focus:ring-[#8B5CF6]/20"
                      />
                      Commercial Invoice
                    </label>
                  </div>
                </div>

                {/* Previous Origin (for circumvention detection) */}
                <div className="space-y-2">
                  <Label className="text-slate-700 font-jakarta font-medium text-sm">
                    Previous Origin Country (if changed)
                  </Label>
                  <Select
                    value={originForm.previous_origin || 'none'}
                    onValueChange={(value) => setOriginForm({ ...originForm, previous_origin: value === 'none' ? '' : value })}
                  >
                    <SelectTrigger className="input-modern h-9 text-sm">
                      <SelectValue placeholder="Select if origin changed recently" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None / Not Changed</SelectItem>
                      {COUNTRIES.map((country) => (
                        <SelectItem key={country} value={country}>{country}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  type="submit"
                  className="w-full btn-primary-modern py-6 bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] hover:opacity-90"
                  disabled={originLoading}
                  data-testid="origin-validate-btn"
                >
                  {originLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Validating...
                    </>
                  ) : (
                    <>
                      <Shield className="w-5 h-5 mr-2" />
                      Validate Origin
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Results Panel */}
            <div className="space-y-6">
              {/* Validation Results */}
              <div className="card-modern p-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#6366F1] to-[#A5B4FC] flex items-center justify-center shadow-lg">
                    <AlertTriangle className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h2 className="font-outfit text-xl font-semibold text-slate-900">
                      Risk Assessment
                    </h2>
                    <p className="text-sm text-slate-500 font-jakarta">Origin validation results</p>
                  </div>
                </div>

                {!originResults ? (
                  <div className="text-center py-8 text-slate-400">
                    <Shield className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="font-jakarta">Enter product details to validate origin</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Risk Indicator */}
                    <div className={`rounded-xl p-4 ${
                      originResults.origin_validation.risk_indicator === 'HIGH' ? 'bg-red-50 border-2 border-red-200' :
                      originResults.origin_validation.risk_indicator === 'MEDIUM' ? 'bg-amber-50 border-2 border-amber-200' :
                      originResults.origin_validation.risk_indicator === 'LOW' ? 'bg-yellow-50 border-2 border-yellow-200' :
                      'bg-green-50 border-2 border-green-200'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-slate-600">Origin Risk Indicator</span>
                        <span className={`font-bold text-lg ${
                          originResults.origin_validation.risk_indicator === 'HIGH' ? 'text-red-600' :
                          originResults.origin_validation.risk_indicator === 'MEDIUM' ? 'text-amber-600' :
                          originResults.origin_validation.risk_indicator === 'LOW' ? 'text-yellow-600' :
                          'text-green-600'
                        }`}>
                          {originResults.origin_validation.risk_indicator}
                        </span>
                      </div>
                      <div className="w-full h-3 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all ${
                            originResults.origin_validation.risk_score >= 70 ? 'bg-red-500' :
                            originResults.origin_validation.risk_score >= 40 ? 'bg-amber-500' :
                            originResults.origin_validation.risk_score >= 20 ? 'bg-yellow-500' :
                            'bg-green-500'
                          }`}
                          style={{ width: `${originResults.origin_validation.risk_score}%` }}
                        />
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Risk Score: {originResults.origin_validation.risk_score}/100</p>
                    </div>

                    {/* FTA Eligibility */}
                    <div className={`rounded-xl p-4 ${
                      originResults.fta_eligibility.fta_eligible ? 'bg-green-50' : 'bg-slate-50'
                    }`}>
                      <div className="flex items-center gap-2 mb-2">
                        {originResults.fta_eligibility.fta_eligible ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <X className="w-5 h-5 text-slate-400" />
                        )}
                        <span className="font-medium text-slate-900">
                          {originResults.fta_eligibility.fta_eligible ? 'FTA Eligible' : 'No FTA Available'}
                        </span>
                      </div>
                      {originResults.fta_eligibility.applicable_fta_label && (
                        <p className="text-sm text-slate-600 ml-7" data-testid="origin-fta-label">
                          {originResults.fta_eligibility.applicable_fta_label}
                        </p>
                      )}
                    </div>

                    {/* Tariff Comparison */}
                    <div className="bg-slate-50 rounded-xl p-4">
                      <p className="text-sm font-medium text-slate-700 mb-3">Tariff Comparison</p>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-center p-3 bg-white rounded-lg">
                          <p className="text-xs text-slate-500 mb-1">MFN Rate</p>
                          <p className={`text-2xl font-bold ${
                            originResults.fta_eligibility.mfn_rate > 10 ? 'text-red-600' : 'text-slate-700'
                          }`}>
                            {originResults.fta_eligibility.mfn_rate}%
                          </p>
                          {originResults.fta_eligibility.section_301_applicable && (
                            <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">+Section 301</span>
                          )}
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg">
                          <p className="text-xs text-slate-500 mb-1">Preferential Rate</p>
                          <p className="text-2xl font-bold text-green-600">
                            {originResults.fta_eligibility.preferential_rate !== null 
                              ? `${originResults.fta_eligibility.preferential_rate}%` 
                              : 'N/A'}
                          </p>
                          {originResults.fta_eligibility.tariff_savings > 0 && (
                            <span className="text-xs bg-green-100 text-green-600 px-2 py-0.5 rounded-full">
                              Save {originResults.fta_eligibility.tariff_savings}%
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Identified Risks */}
                    {originResults.origin_validation.identified_risks?.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-slate-700">Identified Risks</p>
                        {originResults.origin_validation.identified_risks.map((risk, idx) => (
                          <div key={idx} className={`rounded-lg p-3 text-sm ${
                            risk.severity === 'high' ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200'
                          }`}>
                            <p className="font-medium text-slate-900">{risk.description}</p>
                            <p className="text-slate-600 mt-1 text-xs">{risk.mitigation}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* FTA Requirements */}
                    {originResults.fta_eligibility.requirements?.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-slate-700">FTA Requirements</p>
                        <ul className="text-sm text-slate-600 space-y-1">
                          {originResults.fta_eligibility.requirements.map((req, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <span className="text-[#8B5CF6] mt-1">•</span>
                              {req}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Scenario Modeling */}
              <div className="card-modern p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F59E0B] to-[#F97316] flex items-center justify-center shadow-lg">
                    <ArrowLeftRight className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-outfit text-lg font-semibold text-slate-900">Scenario Modeling</h3>
                    <p className="text-xs text-slate-500">What if you sourced from elsewhere?</p>
                  </div>
                </div>

                <div className="space-y-3 mb-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-slate-500">Unit Cost ($)</Label>
                      <Input
                        type="number"
                        placeholder="50"
                        value={scenarioForm.unit_cost}
                        onChange={(e) => {
                          setScenarioForm({ ...scenarioForm, unit_cost: e.target.value });
                          setScenarioResults(null); // Clear results when value changes
                        }}
                        className="h-9 text-sm"
                        data-testid="scenario-unit-cost"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Annual Volume</Label>
                      <Input
                        type="number"
                        placeholder="10000"
                        value={scenarioForm.annual_volume}
                        onChange={(e) => {
                          setScenarioForm({ ...scenarioForm, annual_volume: e.target.value });
                          setScenarioResults(null); // Clear results when value changes
                        }}
                        className="h-9 text-sm"
                        data-testid="scenario-annual-volume"
                      />
                    </div>
                  </div>
                  
                  <Button
                    onClick={handleScenarioModeling}
                    className="w-full bg-gradient-to-r from-[#F59E0B] to-[#F97316] hover:opacity-90"
                    disabled={scenarioLoading || !originForm.description}
                    size="sm"
                  >
                    {scenarioLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <TrendingUp className="w-4 h-4 mr-2" />
                    )}
                    Model Scenarios
                  </Button>
                </div>

                {scenarioResults && (
                  <div className="space-y-2 mt-4">
                    {scenarioResults.scenarios?.map((scenario, idx) => (
                      <div 
                        key={idx}
                        className={`rounded-lg p-3 text-sm ${
                          scenario.is_current ? 'bg-slate-100 border border-slate-300' :
                          scenario.vs_current_savings > 0 ? 'bg-green-50 border border-green-200' :
                          'bg-slate-50'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-slate-900">{scenario.country}</span>
                            {scenario.is_current && (
                              <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">Current</span>
                            )}
                            {scenario.fta_eligible && (
                              <span className="text-xs bg-[#8B5CF6]/10 text-[#8B5CF6] px-2 py-0.5 rounded-full">FTA</span>
                            )}
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            scenario.risk_indicator === 'HIGH' ? 'bg-red-100 text-red-600' :
                            scenario.risk_indicator === 'MEDIUM' ? 'bg-amber-100 text-amber-600' :
                            'bg-green-100 text-green-600'
                          }`}>
                            {scenario.risk_indicator}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-slate-500">Rate: {scenario.effective_rate}%</span>
                          {scenario.annual_duty !== null && (
                            <span className="text-slate-500">Duty: ${scenario.annual_duty?.toLocaleString()}</span>
                          )}
                          {!scenario.is_current && scenario.vs_current_savings > 0 && (
                            <span className="font-bold text-green-600">
                              Save ${scenario.vs_current_savings?.toLocaleString()}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
  );
}
